import { useCallback, useEffect, useState } from 'react';

import { mergeQuickFilterSync } from '@sophocles/data-grids/datasource/quickFilterSync';

/**
 * Local "typed" state for a quick-filter input, kept in sync with a
 * DataGridPro's committed quickFilter via its apiRef.
 *
 * Use when:
 *   - the input is uncommitted (debounced or explicit Enter/button) — i.e.
 *     it can diverge from the grid's filter while the user types, and
 *   - the input must still reflect the grid's persisted filter on mount
 *     (so a sessionStorage-restored search doesn't ghost-filter the grid
 *     with an empty-looking input).
 *
 * Pairs naturally with useServerDataGrid: pass `gridState.apiRef`.
 *
 * @param {Object} apiRef                MUI X DataGridPro apiRef
 * @param {*} [readyToken]              optional value that, when it changes,
 *   re-runs the initial sync + subscribe. Needed when the grid mounts AFTER
 *   this hook's first effect (e.g. EntityGrid renders the grid only once its
 *   spec resolves, so on a cold load apiRef.current is still null on first run).
 *   Pass a flag that flips true on the grid's onGridReady.
 * @returns {[string, (next: string) => void, (next?: string) => void, string]}
 *   value     – current typed text (controlled input value)
 *   setValue  – update the typed text (no commit, UI only)
 *   commit    – push the typed text into the grid's quickFilter (triggers
 *               the grid's onFilterModelChange + refetch). Pass `''` or
 *               nothing to clear.
 *   committed – the grid's applied quickFilter text. Diverges from `value`
 *               while the user types; use it for side fetches (stat cards)
 *               that must follow the list, not the keystrokes.
 */
export default function useGridQuickFilter(apiRef, readyToken) {
  const [value, setValue] = useState('');
  const [committed, setCommitted] = useState('');

  useEffect(() => {
    const sync = model => {
      const next = (model?.quickFilterValues || []).join(' ');
      setCommitted(next);
      setValue(prev => mergeQuickFilterSync(prev, next));
    };
    // Initial sync — apiRef.current is populated by the time effects run on a
    // synchronous mount; readyToken re-runs this once a late-mounting grid is live.
    sync(apiRef?.current?.state?.filter?.filterModel);
    const unsubscribe = apiRef?.current?.subscribeEvent?.('filterModelChange', sync);
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [apiRef, readyToken]);

  const commit = useCallback(
    next => {
      const trimmed = (next ?? '').trim();
      apiRef?.current?.setQuickFilterValues?.(trimmed ? trimmed.split(/\s+/) : []);
    },
    [apiRef],
  );

  return [value, setValue, commit, committed];
}
